import { BookmarkAdd, ZoomIn } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  IconButton,
  ImageListItem,
  ImageListItemBar,
  Typography
} from '@mui/material'
import React, { RefObject } from 'react'
import { connect } from 'react-redux'
import CreateReservation from 'components/amenity-booking/create-reservation'
import NivosModal from 'components/nivos-modal'
import NivosRTE from 'components/nivos-rte'
import placeholderImage from 'images/demo2.png'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import State from 'states/types'
import { Amenity } from 'types/amenity-booking'
import { mapOperationsToDispatchProps, srcset } from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
  }
}

export interface NivosGalleryItemProps {
  amenity: Amenity
  cols?: number
  rows?: number
}

type OwnProps = NivosGalleryItemProps

type AppProps = StateProps & DispatchProps & OwnProps

const NivosGalleryItemFC: React.FC<any> = (props: AppProps) => {
  const { amenity, cols, rows } = props
  const containerRef: RefObject<any> = React.createRef()

  const [hover, setHover] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(false)
  const [lineClamp, setLineClamp] = React.useState<number>()

  React.useEffect(() => {
    if (containerRef.current !== null && lineClamp === undefined) {
      setLineClamp(
        Math.round(containerRef.current.getBoundingClientRect().height / 34)
      )
    }
  }, [containerRef])

  const AmenityDescription: React.FC = () => (
    <Box marginTop={2}>
      {(amenity.descriptionRte === null ||
        amenity.descriptionRte.trim() === '') && (
        <Typography
          color={'white'}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: lineClamp,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {amenity.description}
        </Typography>
      )}
      {amenity.descriptionRte !== null &&
        amenity.descriptionRte.trim() !== '' && (
          <NivosRTE
            rteState={amenity.descriptionRte}
            setRteState={_ => null}
            readOnly={true}
            containerProps={{ color: 'white' }}
          />
      )}
    </Box>
  )

  return (
    <ImageListItem
      cols={cols != null ? cols : 1}
      rows={rows != null ? rows : 1}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      ref={containerRef}
    >
      <NivosModal title={'Nivos'} open={open} setOpen={setOpen}>
        <CreateReservation
          selectedAmenity={amenity}
          handleClose={() => setOpen(false)}
        />
      </NivosModal>
      {loading && (
        <CircularProgress
          sx={{ position: 'absolute', left: '40%', top: '40%' }}
        />
      )}
      {!error && (
        <img
          {...srcset(amenity.images[0].url, 121, rows, cols)}
          alt={amenity.name}
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false)
            setError(true)
          }}
        />
      )}
      {error && (
        <img
          src={placeholderImage}
          style={{ filter: 'grayscale(1) blur(1px)' }}
          alt={amenity.name}
        />
      )}
      <ImageListItemBar
        title={
          <Typography color={'white'} variant={'h5'}>
            {amenity.name}
          </Typography>
        }
        subtitle={hover ? <AmenityDescription /> : null}
        actionIcon={
          <IconButton
            sx={{ color: 'white' }}
            onClick={() => {
              setOpen(true)
              setHover(false)
            }}
          >
            {amenity.bookable && <BookmarkAdd color={'inherit'} />}
            {!amenity.bookable && <ZoomIn color={'inherit'} />}
          </IconButton>
        }
        sx={{
          transition: '.5s',
          height: hover ? '100%' : 56,
          cursor: 'pointer'
        }}
        onClick={() => {
          setHover(false)
          setOpen(true)
        }}
      />
    </ImageListItem>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations
})

const NivosGalleryItem = connect(
  mapStateToProps,
  mapDispatchToProps
)(NivosGalleryItemFC) as React.FC<OwnProps>

export default NivosGalleryItem
